import { useState, useEffect } from 'react'
import styled from 'styled-components'
import { api } from '../../../services/api'
import { Spinner, LoadingContainer } from '../../../components/shared/StyledComponents'

const SectionTitle = styled.h2`
  font-size: ${({ theme }) => theme.fontSizes.md};
  font-weight: 600;
  margin: ${({ theme }) => theme.spacing.xl} 0 ${({ theme }) => theme.spacing.md} 0;
  padding-bottom: ${({ theme }) => theme.spacing.sm};
  border-bottom: 2px solid ${({ theme }) => theme.colors.secondary};
`

const AlertsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`

const AlertCard = styled.div<{ $level: 'ok' | 'warning' | 'critical' | 'unknown' }>`
  background: ${({ $level }) =>
        $level === 'critical' ? 'linear-gradient(135deg, #FFEBEE, #fff)' :
            $level === 'warning' ? 'linear-gradient(135deg, #FFF8E1, #fff)' :
                $level === 'ok' ? 'linear-gradient(135deg, #E8F5E9, #fff)' :
                    'linear-gradient(135deg, #F5F5F5, #fff)'};
  border: 2px solid ${({ $level }) =>
        $level === 'critical' ? '#EF5350' :
            $level === 'warning' ? '#FFA726' :
                $level === 'ok' ? '#66BB6A' :
                    '#BDBDBD'};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.md};
  position: relative;
  overflow: hidden;
`

const AlertBadge = styled.div<{ $level: 'ok' | 'warning' | 'critical' | 'unknown' }>`
  position: absolute;
  top: 8px;
  right: 8px;
  background: ${({ $level }) =>
        $level === 'critical' ? '#EF5350' :
            $level === 'warning' ? '#FFA726' :
                $level === 'ok' ? '#66BB6A' :
                    '#BDBDBD'};
  color: white;
  padding: 4px 12px;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-size: ${({ theme }) => theme.fontSizes.xs};
  font-weight: 600;
  text-transform: uppercase;
`

const ProjectName = styled.h3`
  margin: 0 0 ${({ theme }) => theme.spacing.sm} 0;
  font-size: ${({ theme }) => theme.fontSizes.md};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  padding-right: 100px; // Space for badge
`

const ProjectCode = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.xs};
  color: ${({ theme }) => theme.colors.textMuted};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  font-family: monospace;
`

const HoursInfo = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-top: ${({ theme }) => theme.spacing.md};
`

const HoursStat = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const HoursLabel = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.xs};
  color: ${({ theme }) => theme.colors.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const HoursValue = styled.div<{ $highlight?: boolean }>`
  font-size: ${({ theme }) => theme.fontSizes.lg};
  font-weight: 700;
  color: ${({ $highlight, theme }) => $highlight ? '#EF5350' : theme.colors.text};
`

const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background: #E0E0E0;
  border-radius: 4px;
  overflow: hidden;
  margin-top: ${({ theme }) => theme.spacing.md};
`

const Progress = styled.div<{ $percent: number; $level: 'ok' | 'warning' | 'critical' }>`
  height: 100%;
  width: ${({ $percent }) => $percent}%;
  background: ${({ $level }) =>
        $level === 'critical' ? '#EF5350' :
            $level === 'warning' ? '#FFA726' :
                '#66BB6A'};
  transition: width 0.3s ease;
`

const SummaryCards = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.lg};

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`

const SummaryCard = styled.div<{ $variant: 'critical' | 'warning' | 'ok' }>`
  background: white;
  border: 2px solid ${({ $variant }) =>
        $variant === 'critical' ? '#EF5350' :
            $variant === 'warning' ? '#FFA726' :
                '#66BB6A'};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.md};
  text-align: center;
`

const SummaryValue = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.xxl};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text};
`

const SummaryLabel = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.colors.textMuted};
  margin-top: ${({ theme }) => theme.spacing.xs};
`

const UnavailableBanner = styled.div`
  margin: 0 0 ${({ theme }) => theme.spacing.md} 0;
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  background: #FFEBEE;
  border-left: 4px solid #EF5350;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  color: #B71C1C;
  font-size: ${({ theme }) => theme.fontSizes.sm};
`

const EmptyState = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing.xl};
  color: ${({ theme }) => theme.colors.textMuted};
`

interface ProjectHoursAlert {
    opportunity_id?: number
    project_name: string
    redmine_code: string
    total_hours_available?: number
    /** null when Redmine could not be read (unknown, not zero) */
    hours_used: number | null
    hours_remaining?: number
    hours_planned: number
    alert_level: 'ok' | 'warning' | 'critical' | 'unknown'
    last_sync_at?: string
    // month-end forecast
    redmine_project_id?: number | null
    parent_redmine_code?: string | null
    is_subproject?: boolean
    tipo_proyecto?: string | null
    budget_source?: string | null
    hours_logged_to_date?: number | null
    hours_planned_month?: number
    hours_planned_rest_of_month?: number
    projected_end_of_month?: number | null
    needs_extension?: boolean
    extension_hours?: number
    message?: string | null
    /** Why Redmine could not be read for this project (the level is then 'unknown') */
    redmine_error?: string | null
}

export interface BillingAlertsResponse {
    alerts: ProjectHoursAlert[]
    /** Projects (not nodes) whose worst project/subproject is critical */
    critical_count: number
    warning_count: number
    last_updated: string
    /** false when some project could not be read from Redmine */
    redmine_available?: boolean
    redmine_message?: string | null
}

/** Banner text when the forecast could not read Redmine, so "unknown" is never mistaken for "no bag / 0 h". */
export const unavailableMessage = (data: BillingAlertsResponse | null): string | null => {
    if (!data || data.redmine_available !== false) return null
    return `⚠️ Redmine no disponible: la previsión no está completa${data.redmine_message ? ` (${data.redmine_message})` : ''}.`
}

export const BillingAlertsSection = () => {
    const [loading, setLoading] = useState(true)
    const [alertsData, setAlertsData] = useState<BillingAlertsResponse | null>(null)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        fetchAlerts()
    }, [])

    const fetchAlerts = async () => {
        try {
            setLoading(true)
            const response = await api.billing.getAlerts()
            setAlertsData(response.data)
            setError(null)
        } catch (err: any) {
            console.error('Error fetching billing alerts:', err)
            setError(err.response?.data?.detail || 'Error al cargar alertas de imputación')
        } finally {
            setLoading(false)
        }
    }

    const getUsedPercent = (alert: ProjectHoursAlert): number => {
        if (!alert.total_hours_available) return 0
        return ((alert.hours_used ?? 0) / alert.total_hours_available) * 100
    }

    const getAlertText = (level: string): string => {
        switch (level) {
            case 'critical': return 'Crítico'
            case 'warning': return 'Alerta'
            case 'ok': return 'OK'
            default: return 'Desconocido'
        }
    }

    const banner = unavailableMessage(alertsData)

    if (loading) {
        return (
            <LoadingContainer>
                <Spinner />
                <p>Cargando alertas de imputación...</p>
            </LoadingContainer>
        )
    }

    if (error) {
        return (
            <>
                <SectionTitle>📊 Imputación - Horas de Proyecto</SectionTitle>
                <EmptyState>{error}</EmptyState>
            </>
        )
    }

    if (!alertsData || alertsData.alerts.length === 0) {
        return (
            <>
                <SectionTitle>📊 Imputación - Horas de Proyecto</SectionTitle>
                {banner ? <UnavailableBanner>{banner}</UnavailableBanner> : (
                    <EmptyState>
                        ✅ No hay alertas. Todos los proyectos tienen horas disponibles.
                    </EmptyState>
                )}
            </>
        )
    }

    return (
        <>
            <SectionTitle>📊 Imputación - Horas de Proyecto</SectionTitle>
            {banner && <UnavailableBanner>{banner}</UnavailableBanner>}

            <SummaryCards>
                <SummaryCard $variant="critical">
                    <SummaryValue>{alertsData.critical_count}</SummaryValue>
                    <SummaryLabel>Proyectos Críticos</SummaryLabel>
                </SummaryCard>
                <SummaryCard $variant="warning">
                    <SummaryValue>{alertsData.warning_count}</SummaryValue>
                    <SummaryLabel>Proyectos en Alerta</SummaryLabel>
                </SummaryCard>
                <SummaryCard $variant="ok">
                    <SummaryValue>{alertsData.alerts.filter(a => a.alert_level === 'ok').length}</SummaryValue>
                    <SummaryLabel>Proyectos OK</SummaryLabel>
                </SummaryCard>
            </SummaryCards>

            <AlertsGrid>
                {alertsData.alerts.map((alert, index) => (
                    <AlertCard key={index} $level={alert.alert_level}>
                        <AlertBadge $level={alert.alert_level}>
                            {alert.redmine_error ? 'Sin datos' : getAlertText(alert.alert_level)}
                        </AlertBadge>

                        <ProjectName>{alert.is_subproject ? '↳ ' : ''}{alert.project_name}</ProjectName>
                        <ProjectCode>
                            {alert.redmine_code}
                            {alert.parent_redmine_code ? ` · subproyecto de ${alert.parent_redmine_code}` : ''}
                            {alert.tipo_proyecto ? ` · ${alert.tipo_proyecto}` : ''}
                        </ProjectCode>
                        {alert.needs_extension && (
                            <div style={{ margin: '6px 0', padding: '6px 10px', background: '#FFEBEE', border: '1px solid #EF9A9A', borderRadius: 6, color: '#B71C1C', fontWeight: 600, fontSize: '0.85rem' }}>
                                🚨 Pedir ampliación: +{(alert.extension_hours || 0).toFixed(1)}h
                            </div>
                        )}
                        {alert.message && (
                            <div style={{ margin: '4px 0 8px', fontSize: '0.78rem', color: '#555' }}>{alert.message}</div>
                        )}

                        <HoursInfo>
                            <HoursStat>
                                <HoursLabel>Habilitadas</HoursLabel>
                                <HoursValue>
                                    {alert.total_hours_available?.toFixed(1) || 'N/A'}h
                                </HoursValue>
                            </HoursStat>
                            <HoursStat>
                                <HoursLabel>Imputadas</HoursLabel>
                                <HoursValue>
                                    {alert.hours_used != null ? `${alert.hours_used.toFixed(1)}h` : 'N/D'}
                                </HoursValue>
                            </HoursStat>
                            <HoursStat>
                                <HoursLabel>Restantes</HoursLabel>
                                <HoursValue $highlight={alert.alert_level === 'critical'}>
                                    {alert.hours_remaining?.toFixed(1) || 'N/A'}h
                                </HoursValue>
                            </HoursStat>
                            <HoursStat>
                                <HoursLabel>Previsto resto de mes</HoursLabel>
                                <HoursValue>
                                    {alert.hours_planned.toFixed(1)}h
                                </HoursValue>
                            </HoursStat>
                            <HoursStat>
                                <HoursLabel>Proyección fin de mes</HoursLabel>
                                <HoursValue $highlight={!!alert.needs_extension}>
                                    {alert.projected_end_of_month != null ? `${alert.projected_end_of_month.toFixed(1)}h` : 'N/A'}
                                </HoursValue>
                            </HoursStat>
                        </HoursInfo>

                        {alert.total_hours_available && (
                            <ProgressBar>
                                <Progress
                                    $percent={getUsedPercent(alert)}
                                    $level={alert.alert_level as 'ok' | 'warning' | 'critical'}
                                />
                            </ProgressBar>
                        )}
                    </AlertCard>
                ))}
            </AlertsGrid>
        </>
    )
}
